import { useState, useEffect, useMemo } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { Phone, FileText, Menu, X, Calculator } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';

const NAV_ITEMS = [
  { to: '/', label: 'Accueil', end: true },
  { to: '/le-groupe', label: 'Le Groupe' },
  { to: '/implantations', label: 'Implantations' },
  { to: '/flotte', label: 'Notre Flotte' },
  { to: '/services', label: 'Services' },
  { to: '/transports-specialises', label: 'Spécialisés' },
  { to: '/estimation-transport', label: 'Estimer' },
  { to: '/avis', label: 'Avis' },
  { to: '/recrutement', label: 'Recrutement' },
  { to: '/contact', label: 'Contact' },
];

const DEFAULT_PHONE = { display: '04 70 42 72 63', tel: '+33470427263' };

// Contextualise le numéro selon la landing consultée (l'utilisateur voit d'emblée
// le bon numéro pour l'implantation qu'il visite — meilleur taux de conversion).
const CONTEXTUAL_PHONES = {
  '/ambulance-03-avermes':          { display: '06 56 66 05 71', tel: '+33656660571' },
  '/ambulance-beaulon-garnat':      { display: '04 70 42 72 63', tel: '+33470427263' },
  '/celine-ambulance-diou':         { display: '04 70 42 90 08', tel: '+33470429008' },
  '/ambulance-tronget-le-montet':   { display: '04 70 47 10 30', tel: '+33470471030' },
};

export default function Header() {
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => { setOpen(false); }, [location]);

  // Choisit le numéro contextuel selon la route active
  const activePhone = useMemo(() => {
    return CONTEXTUAL_PHONES[location.pathname] || DEFAULT_PHONE;
  }, [location.pathname]);

  return (
    <header className="sticky top-0 z-50 bg-white/85 backdrop-blur supports-[backdrop-filter]:bg-white/70 border-b border-border">
      <div className="container-app flex items-center gap-3 py-3">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-3 focus-ring rounded-lg shrink-0" data-testid="header-logo-link">
          <div className="relative h-11 w-11 sm:h-12 sm:w-12 rounded-xl bg-gradient-to-br from-brand-600 to-brand-900 flex items-center justify-center shadow-btn ring-1 ring-brand-700/20">
            <span className="text-white font-heading font-bold text-lg sm:text-xl">B</span>
            <span className="absolute -top-1 -right-1 h-3 w-3 rounded-full bg-emerald-500 ring-2 ring-white"></span>
          </div>
          <div className="flex flex-col leading-none">
            <span className="font-heading font-semibold text-anthracite-900 text-sm sm:text-base tracking-tight">Groupe Ambulances</span>
            <span className="font-heading font-extrabold text-brand-800 text-base sm:text-lg tracking-tight -mt-0.5">Beaulon</span>
          </div>
        </Link>

        {/* Desktop nav */}
        <nav className="hidden lg:flex items-center gap-0.5 ml-4 xl:ml-6" aria-label="Navigation principale">
          {NAV_ITEMS.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              className={({ isActive }) =>
                `relative px-2.5 xl:px-3 py-2 text-[13px] xl:text-[14px] font-medium rounded-md transition-colors ${
                  isActive
                    ? 'text-brand-700 bg-brand-50'
                    : 'text-anthracite-700 hover:text-brand-700 hover:bg-anthracite-100'
                }`
              }
              data-testid={`nav-link-${item.to.replace('/', '') || 'home'}`}
            >
              {item.label}
            </NavLink>
          ))}
        </nav>

        {/* CTA cluster */}
        <div className="ml-auto flex items-center gap-2">
          <a
            href={`tel:${activePhone.tel}`}
            className="hidden md:inline-flex items-center gap-2 whitespace-nowrap rounded-lg bg-brand-500 px-3.5 xl:px-4 py-2 text-white text-[13px] xl:text-sm font-medium shadow-btn hover:bg-brand-600 transition-colors focus-ring"
            data-testid="header-call-button"
            aria-label={`Appeler ${activePhone.display}`}
          >
            <Phone className="h-4 w-4" />
            {activePhone.display}
          </a>
          <Link to="/devis" className="hidden md:inline-flex">
            <Button variant="outline" size="sm" data-testid="header-quote-button">
              <FileText className="h-4 w-4" /> Devis
            </Button>
          </Link>

          {/* Mobile menu */}
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <Button variant="outline" size="icon" className="lg:hidden" data-testid="mobile-menu-button" aria-label="Ouvrir le menu">
                {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-full sm:w-[360px] p-0" data-testid="mobile-nav-sheet">
              <div className="flex flex-col h-full">
                <div className="px-6 py-5 border-b">
                  <div className="flex items-center gap-3">
                    <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-brand-600 to-brand-900 flex items-center justify-center shadow-btn">
                      <span className="text-white font-heading font-bold text-xl">B</span>
                    </div>
                    <div className="flex flex-col leading-none">
                      <span className="font-heading font-semibold text-anthracite-900 text-sm tracking-tight">Groupe Ambulances</span>
                      <span className="font-heading font-extrabold text-brand-800 text-base tracking-tight -mt-0.5">Beaulon</span>
                    </div>
                  </div>
                </div>
                <nav className="flex-1 overflow-y-auto px-3 py-3" aria-label="Navigation mobile">
                  {NAV_ITEMS.map((item) => (
                    <NavLink
                      key={item.to}
                      to={item.to}
                      end={item.end}
                      className={({ isActive }) =>
                        `block px-4 py-3 rounded-lg text-[15px] font-medium ${
                          isActive ? 'bg-brand-50 text-brand-700' : 'text-anthracite-900 hover:bg-anthracite-100'
                        }`
                      }
                      data-testid={`mobile-nav-link-${item.to.replace('/', '') || 'home'}`}
                    >
                      {item.label === 'Estimer' ? (
                        <span className="inline-flex items-center gap-2"><Calculator className="h-4 w-4" /> {item.label}</span>
                      ) : item.label}
                    </NavLink>
                  ))}
                </nav>
                <div className="p-4 border-t bg-anthracite-100/40 space-y-2">
                  <a
                    href={`tel:${activePhone.tel}`}
                    className="flex items-center justify-center gap-2 rounded-lg bg-brand-500 px-4 py-3 text-white font-medium shadow-btn hover:bg-brand-600"
                    data-testid="mobile-call-button"
                  >
                    <Phone className="h-5 w-5" /> Appeler · {activePhone.display}
                  </a>
                  <Link to="/devis" className="flex items-center justify-center gap-2 rounded-lg border bg-white px-4 py-3 font-medium text-anthracite-900 hover:bg-anthracite-100">
                    <FileText className="h-5 w-5" /> Demander un devis
                  </Link>
                </div>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
}
